import React, { useState, useEffect, useCallback } from 'react';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import { supabase } from '../../lib/supabase';
import './DashboardSettings.css';

const DashboardSettings = () => {
  const { refreshSettings } = useSystemSettings();
  const [systemSettings, setSystemSettings] = useState({
    available_grades: [],
    available_languages: [],
    available_subjects: [],
    social_links_enabled: true,
    social_links: {
      instagram: { enabled: false, url: '', enabled_at: null },
      facebook: { enabled: false, url: '', enabled_at: null },
      youtube: { enabled: false, url: '', enabled_at: null },
      linkedin: { enabled: false, url: '', enabled_at: null },
      twitter: { enabled: false, url: '', enabled_at: null }
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form states
  const [newGrade, setNewGrade] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newSubject, setNewSubject] = useState('');

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Collapsible sections state
  const [socialLinksExpanded, setSocialLinksExpanded] = useState(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(false);
  const [global404Enabled, setGlobal404Enabled] = useState(false);

  // Helper function to normalize grades format (convert old text array to new JSONB format)
  const normalizeGrades = (grades) => {
    if (!grades || !Array.isArray(grades)) {
      return [];
    }

    // Check if it's already in the new format (array of objects with grade and order)
    if (grades.length > 0 && typeof grades[0] === 'object') {
      // Handle both "name" (old) and "grade" (new) field names for backward compatibility
      const hasGradeField = grades[0].grade !== undefined;
      const hasNameField = grades[0].name !== undefined;

      if (hasGradeField || hasNameField) {
        return grades.map((grade, index) => {
          // Handle double-encoded JSON strings in name field
          let gradeName = grade.grade || grade.name;

          // If name/grade is a JSON string, try to parse it
          if (typeof gradeName === 'string' && gradeName.startsWith('{')) {
            try {
              const parsed = JSON.parse(gradeName);
              gradeName = parsed.grade || parsed.name || gradeName;
            } catch (e) {
              // If parsing fails, use as-is
            }
          }

          return {
            grade: gradeName,
            order: grade.order !== undefined ? grade.order : index
          };
        }).sort((a, b) => a.order - b.order);
      }
    }

    // Old format (text array), convert to new format
    return grades.map((grade, index) => ({
      grade: typeof grade === 'string' ? grade : (grade.grade || grade.name || ''),
      order: index
    }));
  };

  // Fetch system settings
  const fetchSystemSettings = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch from system_settings table (only existing columns)
      const { data, error } = await supabase
        .from('system_settings')
        .select('available_grades, available_languages, available_subjects')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      const { data: adminSettings, error: adminSettingsError } = await supabase
        .from('admin_settings')
        .select('"404"')
        .eq('id', true)
        .maybeSingle();

      if (!adminSettingsError) {
        setGlobal404Enabled(adminSettings?.['404'] === true);
      }

      // Use data from database or defaults
      // Normalize grades to new JSONB format with order indices
      const rawGrades = data?.available_grades || [];
      const grades = normalizeGrades(rawGrades);
      const languages = data?.available_languages || [];
      const subjects = data?.available_subjects || [];

      // Get social links setting from localStorage or default to true
      const localSettings = localStorage.getItem('system_settings');
      const parsedLocalSettings = localSettings ? JSON.parse(localSettings) : {};
      const socialLinksEnabled = parsedLocalSettings.social_links_enabled !== false; // Default to true

      // Get social links configuration
      const defaultSocialLinks = {
        instagram: { enabled: false, url: '', enabled_at: null },
        facebook: { enabled: false, url: '', enabled_at: null },
        youtube: { enabled: false, url: '', enabled_at: null },
        linkedin: { enabled: false, url: '', enabled_at: null },
        twitter: { enabled: false, url: '', enabled_at: null }
      };
      // Merge with defaults to ensure enabled_at exists
      const rawSocialLinks = parsedLocalSettings.social_links || data?.social_links || defaultSocialLinks;
      const socialLinks = Object.keys(defaultSocialLinks).reduce((acc, key) => {
        const existingLink = rawSocialLinks[key] || {};
        // If platform is enabled but has no enabled_at, set a timestamp (backward compatibility)
        const enabledAt = existingLink.enabled_at ||
          (existingLink.enabled ? new Date().toISOString() : null);

        acc[key] = {
          ...defaultSocialLinks[key],
          ...existingLink,
          enabled_at: enabledAt
        };
        return acc;
      }, {});

      setSystemSettings({
        available_grades: grades,
        available_languages: languages,
        available_subjects: subjects,
        social_links_enabled: socialLinksEnabled,
        social_links: socialLinks
      });

      // Update localStorage with current data
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: grades,
        available_languages: languages,
        available_subjects: subjects,
        social_links_enabled: socialLinksEnabled,
        social_links: socialLinks
      }));

    } catch (err) {setMessage({ type: 'error', text: 'Failed to load settings from database.' });

      // Fallback to localStorage
      const localSettings = localStorage.getItem('system_settings');
      if (localSettings) {
        const parsed = JSON.parse(localSettings);
        const defaultSocialLinks = {
          instagram: { enabled: false, url: '', enabled_at: null },
          facebook: { enabled: false, url: '', enabled_at: null },
          youtube: { enabled: false, url: '', enabled_at: null },
          linkedin: { enabled: false, url: '', enabled_at: null },
          twitter: { enabled: false, url: '', enabled_at: null }
        };
        const rawSocialLinks = parsed.social_links || defaultSocialLinks;
        const socialLinks = Object.keys(defaultSocialLinks).reduce((acc, key) => {
          const existingLink = rawSocialLinks[key] || {};
          // If platform is enabled but has no enabled_at, set a timestamp (backward compatibility)
          const enabledAt = existingLink.enabled_at ||
            (existingLink.enabled ? new Date().toISOString() : null);

          acc[key] = {
            ...defaultSocialLinks[key],
            ...existingLink,
            enabled_at: enabledAt
          };
          return acc;
        }, {});
        // Normalize grades from localStorage too
        const parsedGrades = normalizeGrades(parsed.available_grades || []);
        setSystemSettings({
          available_grades: parsedGrades,
          available_languages: parsed.available_languages || [],
          available_subjects: parsed.available_subjects || [],
          social_links_enabled: parsed.social_links_enabled !== false,
          social_links: socialLinks
        });
      } else {
        // Set default values on error
        setSystemSettings({
          available_grades: [],
          available_languages: [],
          available_subjects: [],
          social_links_enabled: true,
          social_links: {
            instagram: { enabled: false, url: '', enabled_at: null },
            facebook: { enabled: false, url: '', enabled_at: null },
            youtube: { enabled: false, url: '', enabled_at: null },
            linkedin: { enabled: false, url: '', enabled_at: null },
            twitter: { enabled: false, url: '', enabled_at: null }
          }
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Save system settings
  const saveSystemSettings = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Save to system_settings table
      const { error } = await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: systemSettings.available_grades,
          available_languages: systemSettings.available_languages,
          available_subjects: systemSettings.available_subjects,
          social_links: systemSettings.social_links,
          updated_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      const { error: adminSettingsError } = await supabase
        .from('admin_settings')
        .upsert({ id: true, '404': global404Enabled, updated_at: new Date().toISOString() });

      if (adminSettingsError) {
        throw adminSettingsError;
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' });

      // Save to localStorage for future use
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: systemSettings.available_grades,
        available_languages: systemSettings.available_languages,
        available_subjects: systemSettings.available_subjects,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));

      // Refresh the system settings context to update the navbar
      refreshSettings();

    } catch (err) {// Fallback: save to localStorage even if database fails
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: systemSettings.available_grades,
        available_languages: systemSettings.available_languages,
        available_subjects: systemSettings.available_subjects,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));
      setMessage({ type: 'success', text: 'Settings saved. Social media links will be updated immediately.' });
      // Refresh the system settings context to update the navbar
      refreshSettings();
    } finally {
      setSaving(false);
    }
  };

  // Add grade
  const addGrade = async () => {
    const gradeName = newGrade.trim();
    if (!gradeName) return;

    // Check if grade already exists
    const gradeExists = systemSettings.available_grades.some(
      g => (typeof g === 'string' ? g : (g.grade || g.name)) === gradeName
    );

    if (gradeExists) return;

    // Get the highest order index and add 1
    const maxOrder = systemSettings.available_grades.length > 0
      ? Math.max(...systemSettings.available_grades.map(g => typeof g === 'string' ? 0 : g.order))
      : -1;

    const newGradeObj = {
      grade: gradeName,
      order: maxOrder + 1
    };

    const updatedGrades = [...systemSettings.available_grades, newGradeObj];

    setSystemSettings(prev => ({
      ...prev,
      available_grades: updatedGrades
    }));

    setNewGrade('');

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: updatedGrades,
          available_languages: systemSettings.available_languages,
          updated_at: new Date().toISOString()
        });

      // Update localStorage
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: updatedGrades,
        available_languages: systemSettings.available_languages,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));
    } catch (error) {
      }
  };

  // Remove grade
  const removeGrade = async (gradeName) => {
    const updatedGrades = systemSettings.available_grades
      .filter(g => {
        const name = typeof g === 'string' ? g : (g.grade || g.name);
        return name !== gradeName;
      })
      .map((g, index) => ({
        grade: typeof g === 'string' ? g : (g.grade || g.name),
        order: index // Re-index after removal
      }));

    setSystemSettings(prev => ({
      ...prev,
      available_grades: updatedGrades
    }));

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: updatedGrades,
          available_languages: systemSettings.available_languages,
          updated_at: new Date().toISOString()
        });

      // Update localStorage
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: updatedGrades,
        available_languages: systemSettings.available_languages,
        social_links_enabled: systemSettings.social_links_enabled
      }));
    } catch (error) {
      }
  };

  // Handle drag start
  const handleDragStart = (e, displayIndex) => {
    setDraggedIndex(displayIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e, dropDisplayIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropDisplayIndex) {
      setDraggedIndex(null);
      return;
    }

    // Get sorted grades array (as displayed)
    const sortedGrades = [...systemSettings.available_grades].sort((a, b) => {
      const orderA = typeof a === 'string' ? 0 : a.order;
      const orderB = typeof b === 'string' ? 0 : b.order;
      return orderA - orderB;
    });

    const draggedGrade = sortedGrades[draggedIndex];

    // Remove dragged item from sorted array
    sortedGrades.splice(draggedIndex, 1);

    // Insert at new position
    sortedGrades.splice(dropDisplayIndex, 0, draggedGrade);

    // Re-index all grades based on new order
    const reorderedGrades = sortedGrades.map((grade, index) => ({
      grade: typeof grade === 'string' ? grade : (grade.grade || grade.name),
      order: index
    }));

    setSystemSettings(prev => ({
      ...prev,
      available_grades: reorderedGrades
    }));

    setDraggedIndex(null);

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: reorderedGrades,
          available_languages: systemSettings.available_languages,
          updated_at: new Date().toISOString()
        });

      // Update localStorage
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: reorderedGrades,
        available_languages: systemSettings.available_languages,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));
    } catch (error) {
      }
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
  };

  // Add language
  const addLanguage = async () => {
    if (newLanguage.trim() && !systemSettings.available_languages.includes(newLanguage.trim())) {
      const updatedLanguages = [...systemSettings.available_languages, newLanguage.trim()];

      setSystemSettings(prev => ({
        ...prev,
        available_languages: updatedLanguages
      }));

      setNewLanguage('');

      // Auto-save to database
      try {
        await supabase
          .from('system_settings')
          .upsert({
            id: 1,
            available_grades: systemSettings.available_grades,
            available_languages: updatedLanguages,
            updated_at: new Date().toISOString()
          });

        // Update localStorage
        localStorage.setItem('system_settings', JSON.stringify({
          available_grades: systemSettings.available_grades,
          available_languages: updatedLanguages,
          social_links_enabled: systemSettings.social_links_enabled
        }));
      } catch (error) {
        }
    }
  };

  // Remove language
  const removeLanguage = async (language) => {
    const updatedLanguages = systemSettings.available_languages.filter(l => l !== language);

    setSystemSettings(prev => ({
      ...prev,
      available_languages: updatedLanguages
    }));

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: systemSettings.available_grades,
          available_languages: updatedLanguages,
          updated_at: new Date().toISOString()
        });

      // Update localStorage
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: systemSettings.available_grades,
        available_languages: updatedLanguages,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));
    } catch (error) {
      }
  };

  // Add subject
  const addSubject = async () => {
    if (newSubject.trim() && !systemSettings.available_subjects.includes(newSubject.trim())) {
      const updatedSubjects = [...systemSettings.available_subjects, newSubject.trim()];

      setSystemSettings(prev => ({
        ...prev,
        available_subjects: updatedSubjects
      }));

      setNewSubject('');

      // Auto-save to database
      try {
        await supabase
          .from('system_settings')
          .upsert({
            id: 1,
            available_grades: systemSettings.available_grades,
            available_languages: systemSettings.available_languages,
            available_subjects: updatedSubjects,
            updated_at: new Date().toISOString()
          });

        // Update localStorage
        localStorage.setItem('system_settings', JSON.stringify({
          available_grades: systemSettings.available_grades,
          available_languages: systemSettings.available_languages,
          available_subjects: updatedSubjects,
          social_links_enabled: systemSettings.social_links_enabled,
          social_links: systemSettings.social_links
        }));
      } catch (error) {
        }
    }
  };

  // Remove subject
  const removeSubject = async (subject) => {
    const updatedSubjects = systemSettings.available_subjects.filter(s => s !== subject);

    setSystemSettings(prev => ({
      ...prev,
      available_subjects: updatedSubjects
    }));

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: systemSettings.available_grades,
          available_languages: systemSettings.available_languages,
          available_subjects: updatedSubjects,
          updated_at: new Date().toISOString()
        });

      // Update localStorage
      localStorage.setItem('system_settings', JSON.stringify({
        available_grades: systemSettings.available_grades,
        available_languages: systemSettings.available_languages,
        available_subjects: updatedSubjects,
        social_links_enabled: systemSettings.social_links_enabled,
        social_links: systemSettings.social_links
      }));
    } catch (error) {
      }
  };

  // Toggle social links
  const toggleSocialLinks = async () => {
    const newSocialLinksEnabled = !systemSettings.social_links_enabled;

    setSystemSettings(prev => ({
      ...prev,
      social_links_enabled: newSocialLinksEnabled
    }));

    // Save to localStorage immediately
    localStorage.setItem('system_settings', JSON.stringify({
      available_grades: systemSettings.available_grades,
      available_languages: systemSettings.available_languages,
      social_links_enabled: newSocialLinksEnabled,
      social_links: systemSettings.social_links
    }));

    // Update the system settings context to update navbar immediately
    refreshSettings();
  };

  // Toggle individual social platform
  const togglePlatform = async (platform) => {
    const isCurrentlyEnabled = systemSettings.social_links[platform]?.enabled || false;
    const newEnabledState = !isCurrentlyEnabled;

    const updatedLinks = {
      ...systemSettings.social_links,
      [platform]: {
        ...systemSettings.social_links[platform],
        enabled: newEnabledState,
        // Set enabled_at timestamp when enabling, clear when disabling
        enabled_at: newEnabledState ? new Date().toISOString() : null
      }
    };

    const updatedSettings = {
      ...systemSettings,
      social_links: updatedLinks
    };

    setSystemSettings(updatedSettings);

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: systemSettings.available_grades,
          available_languages: systemSettings.available_languages,
          social_links: updatedLinks,
          updated_at: new Date().toISOString()
        });
    } catch (error) {
      }

    // Update localStorage
    localStorage.setItem('system_settings', JSON.stringify({
      available_grades: systemSettings.available_grades,
      available_languages: systemSettings.available_languages,
      social_links_enabled: systemSettings.social_links_enabled,
      social_links: updatedLinks
    }));

    // Refresh the system settings context
    refreshSettings();
  };

  // Update platform URL
  const updatePlatformUrl = async (platform, url) => {
    const updatedLinks = {
      ...systemSettings.social_links,
      [platform]: {
        ...systemSettings.social_links[platform],
        url: url
      }
    };

    const updatedSettings = {
      ...systemSettings,
      social_links: updatedLinks
    };

    setSystemSettings(updatedSettings);

    // Auto-save to database
    try {
      await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          available_grades: systemSettings.available_grades,
          available_languages: systemSettings.available_languages,
          social_links: updatedLinks,
          updated_at: new Date().toISOString()
        });
    } catch (error) {
      }

    // Update localStorage
    localStorage.setItem('system_settings', JSON.stringify({
      available_grades: systemSettings.available_grades,
      available_languages: systemSettings.available_languages,
      social_links_enabled: systemSettings.social_links_enabled,
      social_links: updatedLinks
    }));

    // Refresh the system settings context
    refreshSettings();
  };

  // Trigger manual maintenance
  const handleRunMaintenance = async () => {
    if (!window.confirm('Are you sure you want to run daily maintenance? This will deactivate expired schools and clear their calendar data.')) {
      return;
    }

    setIsMaintenanceLoading(true);
    try {
      const { data, error } = await supabase.rpc('trigger_manual_maintenance');
      if (error) throw error;

      if (data && data.success) {
        setMessage({ type: 'success', text: `Maintenance Started: ${data.message}` });
      } else {
        setMessage({ type: 'error', text: `Maintenance Failed: ${data ? data.error : 'Unknown error'}` });
      }
    } catch (err) {setMessage({ type: 'error', text: `Maintenance Error: ${err.message}` });
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemSettings();
  }, [fetchSystemSettings]);

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>System Settings</h2>
        <p>Manage system-wide settings including available grades, languages, and social media links.</p>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          <span className="message-icon">
            {message.type === 'success' ? '✓' : '⚠️'}
          </span>
          {message.text}
        </div>
      )}

      <div className="settings-sections">
        {/* Social Links Section */}
        <div className="settings-section">
          <div
            className="section-header collapsible-header"
            onClick={() => setSocialLinksExpanded(!socialLinksExpanded)}
          >
            <div className="section-header-content">
              <h3>Social Media Links</h3>
              <p>Control the visibility of social media links in the navigation bar and configure individual platforms</p>
            </div>
            <button
              className="collapse-toggle-btn"
              aria-label={socialLinksExpanded ? 'Collapse section' : 'Expand section'}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`collapse-icon ${socialLinksExpanded ? 'expanded' : ''}`}
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {socialLinksExpanded && (
            <div className="collapsible-content">
              {/* Global Toggle */}
              <div className="toggle-container" style={{ marginBottom: '2rem' }}>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={systemSettings.social_links_enabled}
                    onChange={toggleSocialLinks}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {systemSettings.social_links_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
                <p className="toggle-description">
                  {systemSettings.social_links_enabled
                    ? 'Social media links are visible in the navigation bar'
                    : 'Social media links are hidden from the navigation bar'
                  }
                </p>
              </div>

              {/* Platform Cards */}
              {systemSettings.social_links_enabled && (
                <div className="social-platforms-container">
                  {[
                    {
                      key: 'instagram',
                      name: 'Instagram',
                      color: '#E4405F',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                      )
                    },
                    {
                      key: 'facebook',
                      name: 'Facebook',
                      color: '#1877F2',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      )
                    },
                    {
                      key: 'youtube',
                      name: 'YouTube',
                      color: '#FF0000',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                      )
                    },
                    {
                      key: 'linkedin',
                      name: 'LinkedIn',
                      color: '#0077B5',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      )
                    },
                    {
                      key: 'twitter',
                      name: 'Twitter/X',
                      color: '#1DA1F2',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      )
                    }
                  ].map((platform) => (
                    <div key={platform.key} className="social-platform-card">
                      <div className="platform-header">
                        <div className="platform-info">
                          <span className="platform-icon" style={{ color: platform.color }}>
                            {platform.icon}
                          </span>
                          <span className="platform-name">{platform.name}</span>
                        </div>
                        <label className="platform-toggle-label">
                          <input
                            type="checkbox"
                            checked={systemSettings.social_links[platform.key]?.enabled || false}
                            onChange={() => togglePlatform(platform.key)}
                            className="platform-toggle-input"
                          />
                          <span className="platform-toggle-slider"></span>
                        </label>
                      </div>

                      {systemSettings.social_links[platform.key]?.enabled && (
                        <div className="platform-url-container">
                          <input
                            type="url"
                            value={systemSettings.social_links[platform.key]?.url || ''}
                            onChange={(e) => updatePlatformUrl(platform.key, e.target.value)}
                            placeholder={`Enter ${platform.name} URL (e.g., https://${platform.key}.com/yourprofile)`}
                            className="platform-url-input"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Grades Section */}
        <div className="settings-section">
          <div className="section-header">
            <h3>Available Grades</h3>
            <p>Manage the list of available grades for schools and users. Drag to reorder (order determines promotion sequence).</p>
          </div>

          <div className="add-item-container">
            <input
              type="text"
              value={newGrade}
              onChange={(e) => setNewGrade(e.target.value)}
              placeholder="Enter grade (e.g., Grade 1, Year 7, etc.)"
              className="add-item-input"
              onKeyPress={(e) => e.key === 'Enter' && addGrade()}
            />
            <button
              onClick={addGrade}
              className="add-item-btn"
              disabled={!newGrade.trim()}
            >
              Add Grade
            </button>
          </div>

          <div className="items-badges-container">
            {systemSettings.available_grades.length === 0 ? (
              <p className="empty-state">No grades added yet. Add the first grade above.</p>
            ) : (
              <div className="grades-reorderable-grid">
                {systemSettings.available_grades
                  .sort((a, b) => {
                    const orderA = typeof a === 'string' ? 0 : a.order;
                    const orderB = typeof b === 'string' ? 0 : b.order;
                    return orderA - orderB;
                  })
                  .map((grade, index) => {
                    const gradeName = typeof grade === 'string' ? grade : (grade.grade || grade.name);
                    const gradeOrder = typeof grade === 'string' ? index : grade.order;
                    return (
                      <div
                        key={`${gradeName}-${gradeOrder}`}
                        className={`grade-reorderable-badge ${draggedIndex === index ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        title={`Drag to reorder. Order: ${gradeOrder}`}
                      >

                        <div className="grade-order-badge">{gradeOrder}</div>
                        <span className="grade-badge-text">{gradeName}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeGrade(gradeName);
                          }}
                          className="grade-badge-remove-btn"
                          title="Remove grade"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        {/* Languages Section */}
        <div className="settings-section">
          <div className="section-header">
            <h3>Available Languages</h3>
            <p>Manage the list of available languages for schools and users</p>
          </div>

          <div className="add-item-container">
            <input
              type="text"
              value={newLanguage}
              onChange={(e) => setNewLanguage(e.target.value)}
              placeholder="Enter language (e.g., English, Spanish, French, etc.)"
              className="add-item-input"
              onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
            />
            <button
              onClick={addLanguage}
              className="add-item-btn"
              disabled={!newLanguage.trim()}
            >
              Add Language
            </button>
          </div>

          <div className="items-badges-container">
            {systemSettings.available_languages.length === 0 ? (
              <p className="empty-state">No languages added yet. Add the first language above.</p>
            ) : (
              <div className="badges-grid">
                {systemSettings.available_languages.map((language, index) => (
                  <div key={index} className="language-badge">
                    <span className="badge-text">{language}</span>
                    <button
                      onClick={() => removeLanguage(language)}
                      className="badge-remove-btn"
                      title="Remove language"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Subjects Section */}
        <div className="settings-section">
          <div className="section-header">
            <h3>Available Subjects</h3>
            <p>Manage the list of available subjects for schools (will be auto-added to new schools)</p>
          </div>

          <div className="add-item-container">
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Enter subject (e.g., Mathematics, English, Science, etc.)"
              className="add-item-input"
              onKeyPress={(e) => e.key === 'Enter' && addSubject()}
            />
            <button
              onClick={addSubject}
              className="add-item-btn"
              disabled={!newSubject.trim()}
            >
              Add Subject
            </button>
          </div>

          <div className="items-badges-container">
            {systemSettings.available_subjects.length === 0 ? (
              <p className="empty-state">No subjects added yet. Add the first subject above.</p>
            ) : (
              <div className="badges-grid">
                {systemSettings.available_subjects.map((subject, index) => (
                  <div key={index} className="subject-badge">
                    <span className="badge-text">{subject}</span>
                    <button
                      onClick={() => removeSubject(subject)}
                      className="badge-remove-btn"
                      title="Remove subject"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* System Maintenance Section */}
        <div className="settings-section global-404-setting">
          <div className="section-header">
            <div className="section-header-content">
              <h3>Global 404 page</h3>
              <p>Temporarily replace the complete frontend, CMS, and all protected pages with the maintenance-style 404 page. Changes apply to open browsers instantly.</p>
            </div>
          </div>
          <label className="global-404-toggle">
            <input
              type="checkbox"
              checked={global404Enabled}
              onChange={(event) => setGlobal404Enabled(event.target.checked)}
              disabled={saving}
            />
            <span className="global-404-toggle__track" aria-hidden="true"><span /></span>
            <span>
              <strong>{global404Enabled ? 'Global 404 is enabled' : 'Global 404 is disabled'}</strong>
              <small>{global404Enabled ? 'Saving will immediately hide this CMS too.' : 'The application is operating normally.'}</small>
            </span>
          </label>
        </div>

        {/* System Maintenance Section */}
        <div className="settings-section">
          <div className="section-header">
            <div className="section-header-content">
              <h3>System Maintenance</h3>
              <p>Manually trigger daily maintenance tasks (school expiry, calendar cleanup). This runs automatically every night.</p>
            </div>
          </div>
          <div className="section-content" style={{ padding: '0 0 1.5rem' }}>
            <button
              onClick={handleRunMaintenance}
              disabled={isMaintenanceLoading}
              style={{
                backgroundColor: '#fff',
                border: '1px solid #ef4444',
                color: '#ef4444',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: isMaintenanceLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: isMaintenanceLoading ? 0.7 : 1,
                fontSize: '0.95rem',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => !isMaintenanceLoading && (e.currentTarget.style.backgroundColor = '#fef2f2')}
              onMouseOut={(e) => !isMaintenanceLoading && (e.currentTarget.style.backgroundColor = '#fff')}
            >
              {isMaintenanceLoading ? (
                <>
                  <span className="loading-spinner" style={{ borderTopColor: '#ef4444', width: '16px', height: '16px', margin: 0 }}></span>
                  Processing...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                  </svg>
                  Run Daily Maintenance Now
                </>
              )}
            </button>
            <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>
              ⚠️ This will deactivate unpaid expired schools and clear old calendar data. This runs automatically every midnight.
            </p>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button
          onClick={saveSystemSettings}
          className="save-settings-btn"
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="loading-spinner"></span>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
};

export default DashboardSettings;
